import type {
	API_GetUsersListRequest,
	API_GetUsersListResponse,
	API_UpdateUserRoleRequest,
	API_UpdateUserRoleResponse,
	PaginationMeta,
	UserListItem,
} from "@pilatestime-admin/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

export interface UsersListData {
	users: UserListItem[];
	pagination: PaginationMeta;
}

/**
 * Fetch users list from the API
 */
export const fetchUsers = (
	token: string,
	params?: API_GetUsersListRequest,
): Promise<UsersListData> =>
	api
		.get<API_GetUsersListResponse>("/users", {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params,
		})
		.then((res) => res.data.data);

/**
 * Update a user's role
 */
export const updateUserRole = (
	token: string,
	userId: string,
	data: API_UpdateUserRoleRequest,
): Promise<UserListItem> =>
	api
		.put<API_UpdateUserRoleResponse>(`/users/${userId}/role`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data.data);

/**
 * Query options for fetching users list
 */
export const fetchUsersQueryOptions = (
	token: string,
	params?: API_GetUsersListRequest,
) =>
	queryOptions({
		queryKey: ["users", params],
		queryFn: () => fetchUsers(token, params),
		staleTime: 1 * 60 * 1000, // 1 minute
	});

/**
 * Mutation hook for updating user role
 */
export const useUpdateUserRole = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			userId,
			data,
		}: {
			token: string;
			userId: string;
			data: API_UpdateUserRoleRequest;
		}) => updateUserRole(token, userId, data),
		toastMessages: {
			loading: t("users.toasts.updatingRole"),
			success: t("users.toasts.updateRoleSuccess"),
			error: t("users.toasts.updateRoleError"),
		},
		onSuccess: () => {
			// Invalidate users list to refetch with updated role
			queryClient.invalidateQueries({ queryKey: ["users"] });
		},
	});
};
